import React, { useState } from 'react';

import style from './TaskExecutorComment.module.scss';

import { FrontendTaskStatus, TaskStatus } from 'chaika-frontend-types/frontend-cms';

import { isDefined } from '@utils/isDefined';
import { ButtonModifiers, ButtonTheme } from '@components/Button';
import { SaveButton } from '@components/ActionButtons/SaveButton';
import { translate } from '@chaika/i18n/helper';

import type { DescriptionFieldNames } from '@store/Task';
import { useUpdateTask } from '@common/Task/mutations/taskMutations';
import { useTaskForm } from '@common/Task/TaskRightMenu/context/TaskFormContext';
import { getTaskUpdateParams } from '@common/Task/TaskRightMenu/utils/taskParams';
import { TaskSection } from '../TaskSection';
import { TaskInputSubstitutor } from '../TaskInputSubstitutor';
import { TaskRejectionCommentInput } from './TaskExecutorCommentInput';
import { DescriptionTextInput } from '../DescriptionTextInput';

export function TaskExecutorComment(): React.ReactNode {
    const { state, permissions, actions, task, syncTaskBaseline } = useTaskForm();
    const updateTask = useUpdateTask();

    const { isEditMode, status, rejectionComment, comment } = state;
    const canEditComment = permissions.userCanEditComment && !permissions.isExecuted;
    const canEditRejectionComment = status === TaskStatus.Requested;
    const hasCommentChanges = (comment ?? '').trim().length > 0 && (comment ?? '') !== (task?.comment ?? '');

    const [isSaving, setIsSaving] = useState(false);

    const onChange = ({ target: { value, name } }: React.ChangeEvent<HTMLTextAreaElement>): void => {
        actions.setDescriptionField(name as DescriptionFieldNames, value);
    };

    const onSave = (): void => {
        if (!task) {
            return;
        }

        setIsSaving(true);
        const { id, updateParams } = getTaskUpdateParams(state, task);
        void updateTask
            .mutateAsync({ id, params: updateParams })
            .then(() => syncTaskBaseline({ comment: state.comment }))
            .finally(() => setIsSaving(false));
    };

    return status !== FrontendTaskStatus.Draft && !isEditMode ? (
        <>
            {isDefined(rejectionComment) && (
                <TaskSection showIf={canEditRejectionComment}>
                    <TaskInputSubstitutor
                        canEdit={false}
                        highlight={!canEditRejectionComment}
                        label={translate('executorsComment')}
                        value={rejectionComment}
                    >
                        <TaskRejectionCommentInput
                            value={rejectionComment}
                            onChange={onChange}
                        />
                    </TaskInputSubstitutor>
                </TaskSection>
            )}
            <TaskInputSubstitutor
                canEdit={canEditComment}
                label={translate('executorsComment')}
                value={comment}
            >
                <div className={style.comment}>
                    <DescriptionTextInput
                        dataTest=""
                        disabled={isSaving}
                        placeholder={translate('anyInformationAboutTask')}
                        title={translate('executorsComment')}
                        name="comment"
                        value={comment}
                        onChange={onChange}
                    />
                    <div className={style.saveComment}>
                        <SaveButton
                            withSaveAnimation
                            disabled={!hasCommentChanges}
                            label={translate('saveComment')}
                            theme={ButtonTheme.Secondary}
                            modifiers={[ButtonModifiers.Small]}
                            onSaveClick={onSave}
                        />
                    </div>
                </div>
            </TaskInputSubstitutor>
        </>
    ) : null;
}
